import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type {
  VeeamJob,
  VeeamJobs,
  VeeamObject,
  VeeamObjects,
  VeeamRepositories,
  VeeamRepository,
  VeeamVmEntry,
  VeeamVmProtection,
  BackupPerfRun,
  BackupPerformance,
} from '../api/types'
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageSection,
  SectionedPage,
  Spinner,
  StatCard,
} from '../components/common'
import { DataTable } from '../components/DataTable'
import { useVivo } from '../hooks/useAsync'
import type { AsyncState } from '../hooks/useAsync'
import { NotaCache } from '../components/NotaCache'
import { Connessioni } from '../components/Connessioni'
import { t } from '../i18n/lingua'
import {
  BarreValore,
  Diagnostica,
  EsitoBadge,
  formatSecondi,
  formatVelocita,
  fornitore,
  velocita,
} from '../components/backup'
import { Donut, StatusStrip, formatBytes } from '../components/charts'
import { Meter } from '../components/meters'

function formatGB(gb: number | null): string {
  if (gb == null) return '—'
  if (gb >= 1024) return `${(gb / 1024).toFixed(1)} TB`
  return `${Math.round(gb)} GB`
}

/** L'avviso quando un fornitore configurato non ha risposto: la pagina mostra
 *  gli altri e lo dice, invece di sembrare completa. */
function Parziale({ motivi }: { motivi?: string[] }) {
  if (!motivi?.length) return null
  return <p className="banner banner--warn">{t('Risposta parziale: {motivi}', { motivi: motivi.join(' · ') })}</p>
}

function conProblemi(j: VeeamJob): boolean {
  const r = (j.last_result ?? '').toLowerCase()
  return r === 'failed' || r === 'warning'
}

/**
 * La pagina Backup: lo stato di un Veeam Backup & Replication del cliente, letto
 * in tempo reale attraverso la sonda. Un **cruscotto** in testa e le sezioni —
 * job, repository, oggetti protetti — dietro i menu in alto. Dati volatili, non
 * salvati; colonne filtrabili e ordinabili.
 */
export default function Backup() {
  // La copia del Control Plane apre la pagina subito; «Aggiorna» interroga i
  // sistemi di backup (useVivo: la prima lettura accetta la copia, reload no).
  const jobs = useVivo((fresco) => api.backupJobs(fresco), [])
  const repos = useVivo((fresco) => api.backupRepositories(fresco), [])
  const objects = useVivo((fresco) => api.backupObjects(fresco), [])
  const vm = useVivo((fresco) => api.backupVmProtection(fresco), [])
  const perf = useVivo((fresco) => api.backupPerformance(fresco), [])

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">Backup</h1>
          <p className="page__subtitle">
            {t('Lo stato del backup del cliente (Veeam, Vinchin, Commvault, Rubrik), letto attraverso la sonda. La pagina si apre con i dati dell’ultima lettura; «Aggiorna» interroga di nuovo.')}
          </p>
        </div>
      </header>

      <SectionedPage
        sections={[
          { id: 'cruscotto', label: t('Cruscotto') },
          { id: 'job', label: t('Job di backup') },
          { id: 'prestazioni', label: t('Prestazioni') },
          { id: 'repository', label: t('Repository') },
          { id: 'oggetti', label: t('Oggetti protetti') },
          { id: 'non-protette', label: t('VM non protette') },
          { id: 'connessioni', label: t('Connessioni') },
        ]}
      >
        <PageSection id="cruscotto">
          <Cruscotto jobs={jobs} repos={repos} objects={objects} vm={vm} />
        </PageSection>
        <PageSection id="job">
          <JobsCard dati={jobs} />
        </PageSection>
        <PageSection id="prestazioni">
          <PrestazioniCard dati={perf} />
        </PageSection>
        <PageSection id="repository">
          <RepositoriesCard dati={repos} />
        </PageSection>
        <PageSection id="oggetti">
          <ObjectsCard dati={objects} />
        </PageSection>
        <PageSection id="non-protette">
          <NonProtetteCard dati={vm} />
        </PageSection>
        <PageSection id="connessioni">
          <Connessioni
            funzioni={['backup']}
            segnaposto="veeam-sede"
            onChanged={() => {
              jobs.reload()
              repos.reload()
              objects.reload()
              vm.reload()
            }}
          />
        </PageSection>
      </SectionedPage>
    </>
  )
}

function Cruscotto({
  jobs,
  repos,
  objects,
  vm,
}: {
  jobs: AsyncState<VeeamJobs>
  repos: AsyncState<VeeamRepositories>
  objects: AsyncState<VeeamObjects>
  vm: AsyncState<VeeamVmProtection>
}) {
  const caricando = jobs.loading || repos.loading || objects.loading
  const jrows = jobs.data?.jobs ?? []
  const rrows = repos.data?.repositories ?? []
  const orows = objects.data?.objects ?? []

  const problemi = jrows.filter(conProblemi).length
  const falliti = jrows.filter((j) => (j.last_result ?? '').toLowerCase() === 'failed').length
  const capacita = rrows.reduce((s, r) => s + (r.capacity_gb ?? 0), 0)
  const libero = rrows.reduce((s, r) => s + (r.free_gb ?? 0), 0)

  const reason =
    (jobs.data && !jobs.data.available && jobs.data.reason) ||
    (repos.data && !repos.data.available && repos.data.reason) ||
    (objects.data && !objects.data.available && objects.data.reason) ||
    null

  return (
    <Card
      title={t('Cruscotto')}
      actions={
        <button
          className="secondary"
          onClick={() => {
            jobs.reload()
            repos.reload()
            objects.reload()
            vm.reload()
          }}
          disabled={caricando}
        >
          {caricando ? t('Aggiorno…') : t('Aggiorna')}
        </button>
      }
    >
      <ErrorBanner error={jobs.error ?? repos.error ?? objects.error} />
      <Parziale motivi={jobs.data?.partial} />
      <NotaCache dati={jobs.data} />
      {caricando && !jobs.data ? (
        <Spinner label={t('Interrogo il backup…')} />
      ) : reason ? (
        <p className="muted">{reason}</p>
      ) : (
        <div className="stats">
          <StatCard label={t('Job di backup')} value={jrows.length} />
          <StatCard
            label={t('Job con problemi')}
            value={problemi}
            tone={falliti > 0 ? 'error' : problemi > 0 ? 'warn' : 'ok'}
            hint={falliti > 0 ? t('{n} falliti', { n: falliti }) : undefined}
          />
          <StatCard label={t('Oggetti protetti')} value={orows.length} />
          <StatCard label={t('Repository')} value={rrows.length} />
          <StatCard label={t('Capacità totale')} value={formatGB(capacita || null)} />
          <StatCard
            label={t('Spazio libero')}
            value={formatGB(libero || null)}
            tone={capacita > 0 && libero / capacita < 0.1 ? 'warn' : undefined}
          />
        </div>
      )}
      {!reason && jobs.data && (
        <div className="cruscotto-grafici">
          <section>
            <h3 className="guida__titolo">{t('Esito dell’ultima esecuzione')}</h3>
            <StatusStrip
              emptyHint={t('Nessun job con un esito.')}
              data={[
                { key: 'ok', label: t('Successo'), value: conEsito(jrows, 'success'), tone: 'ok' },
                { key: 'warn', label: t('Avviso'), value: conEsito(jrows, 'warning'), tone: 'warn' },
                { key: 'err', label: t('Fallito'), value: conEsito(jrows, 'failed'), tone: 'error' },
                { key: 'canc', label: t('Annullato'), value: conEsito(jrows, 'canceled'), tone: 'info' },
              ]}
            />
            {jrows.length - conEsito(jrows) > 0 && (
              <p className="field__hint">
                {t('{n} job senza un esito (mai eseguiti, in corso o senza storico).', { n: jrows.length - conEsito(jrows) })}
              </p>
            )}
          </section>
          <section>
            <h3 className="guida__titolo">{t('VM della CMDB protette')}</h3>
            {vm.data?.available && vm.data.total ? (
              <Donut
                centreLabel={t('VM')}
                slices={[
                  { key: 'p', label: t('Protette'), value: vm.data.counts?.protected ?? 0, color: 'var(--ok)' },
                  { key: 'u', label: t('Senza backup'), value: vm.data.counts?.unprotected ?? 0, color: 'var(--error)' },
                ]}
              />
            ) : (
              <p className="muted">{vm.loading ? t('Interrogo il backup…') : t('Nessuna VM da confrontare.')}</p>
            )}
          </section>
          <section>
            <h3 className="guida__titolo">{t('Occupazione delle repository')}</h3>
            {rrows.filter((r) => r.capacity_gb).length ? (
              rrows
                .filter((r) => r.capacity_gb)
                .map((r) => (
                  <Meter
                    key={`${r.vendor ?? ''}-${r.id}`}
                    label={r.name ?? r.id}
                    used={r.used_gb}
                    limit={r.capacity_gb}
                    percent={
                      r.used_gb != null && r.capacity_gb ? Math.round((r.used_gb / r.capacity_gb) * 100) : null
                    }
                    format={(n) => formatGB(n)}
                  />
                ))
            ) : (
              <p className="muted">{t('Nessuna repository con la capacità nota.')}</p>
            )}
          </section>
        </div>
      )}
    </Card>
  )
}

/** Quanti job hanno quell'esito all'ultima esecuzione (senza esito: tutti quelli che ne hanno uno). */
function conEsito(jobs: VeeamJob[], esito?: string): number {
  return jobs.filter((j) => {
    const r = (j.last_result ?? '').toLowerCase()
    return esito ? r === esito : Boolean(r) && r !== 'none'
  }).length
}

/** Le prestazioni di un job nelle esecuzioni recenti. */
interface PrestazioniJob {
  key: string
  vendor?: string
  job_id: string
  nome: string
  esecuzioni: number
  riuscite: number
  concluse: number
  durataMedia: number | null
  ultimaDurata: number | null
  velocitaMedia: number | null
  dati: number | null
  ultima: BackupPerfRun
}

function perJob(runs: BackupPerfRun[]): PrestazioniJob[] {
  const gruppi = new Map<string, BackupPerfRun[]>()
  for (const r of runs) {
    const chiave = `${r.vendor ?? ''}|${r.job_id ?? r.job_name ?? ''}`
    if (!r.job_id && !r.job_name) continue
    gruppi.set(chiave, [...(gruppi.get(chiave) ?? []), r])
  }
  const media = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
  return [...gruppi.entries()].map(([key, rs]) => {
    const ordinate = [...rs].sort((a, b) => (b.start ?? '').localeCompare(a.start ?? ''))
    const concluse = rs.filter((r) => r.result)
    const durate = rs.map((r) => r.duration_s).filter((d): d is number => d != null)
    const velocita_ = rs.map(velocita).filter((v): v is number => v != null)
    const byte = rs.map((r) => r.processed_bytes).filter((b): b is number => b != null)
    return {
      key,
      vendor: ordinate[0].vendor,
      job_id: ordinate[0].job_id ?? ordinate[0].job_name ?? '',
      nome: ordinate[0].job_name ?? ordinate[0].job_id ?? '',
      esecuzioni: rs.length,
      riuscite: concluse.filter((r) => (r.result ?? '').toLowerCase() === 'success').length,
      concluse: concluse.length,
      durataMedia: media(durate),
      ultimaDurata: ordinate[0].duration_s,
      velocitaMedia: media(velocita_),
      dati: byte.length ? byte.reduce((a, b) => a + b, 0) : null,
      ultima: ordinate[0],
    }
  })
}

/**
 * Le prestazioni dei job: le esecuzioni recenti di tutti i fornitori riassunte
 * per job — quante, quante riuscite, quanto durano, a che velocità (GB/h:
 * dati elaborati sulla durata, quando il prodotto dà i byte) — con il
 * confronto fra job su velocità e durata. Il nome porta alla pagina del job.
 */
function PrestazioniCard({ dati }: { dati: AsyncState<BackupPerformance> }) {
  const d = dati.data
  const runs = d?.runs ?? []
  const jobs = perJob(runs)
  const link = (j: PrestazioniJob) => (
    <Link
      to={`/backup/jobs/${encodeURIComponent(j.vendor ?? 'veeam')}/${encodeURIComponent(j.job_id)}?name=${encodeURIComponent(j.nome)}`}
    >
      {j.nome}
    </Link>
  )
  return (
    <Card
      title={t('Prestazioni dei job')}
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Aggiorno…') : t('Aggiorna')}
        </button>
      }
    >
      <ErrorBanner error={dati.error} />
      <Parziale motivi={d?.partial} />
      <NotaCache dati={d} />
      {dati.loading && !d ? (
        <Spinner label={t('Interrogo il backup…')} />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Backup non interrogabile.')}</p>
      ) : !jobs.length ? (
        <EmptyState title={t('Nessuna esecuzione recente')} hint={t('Il sistema di backup non ha esecuzioni negli ultimi giorni, oppure l’utenza non le vede.')} />
      ) : (
        <>
          <div className="stats">
            <StatCard label={t('Esecuzioni')} value={runs.length} />
            <StatCard
              label={t('Riuscite')}
              value={(() => {
                const c = runs.filter((r) => r.result).length
                const ok = runs.filter((r) => (r.result ?? '').toLowerCase() === 'success').length
                return c ? `${Math.round((ok / c) * 100)}%` : '—'
              })()}
            />
            <StatCard
              label={t('Dati elaborati')}
              value={formatBytes(runs.reduce((a, r) => a + (r.processed_bytes ?? 0), 0))}
            />
          </div>
          <div className="cruscotto-grafici">
            <section>
              <h3 className="guida__titolo">{t('Velocità media per job (GB/h)')}</h3>
              <BarreValore
                voci={jobs
                  .filter((j) => j.velocitaMedia != null)
                  .map((j) => ({ key: j.key, label: link(j), value: j.velocitaMedia as number, title: j.nome }))}
                formato={(v) => formatVelocita(v)}
              />
            </section>
            <section>
              <h3 className="guida__titolo">{t('Durata media per job')}</h3>
              <BarreValore
                voci={jobs
                  .filter((j) => j.durataMedia != null)
                  .map((j) => ({ key: j.key, label: link(j), value: j.durataMedia as number, title: j.nome }))}
                formato={(v) => formatSecondi(Math.round(v))}
              />
            </section>
          </div>
          <DataTable
            rows={jobs}
            initialSort={{ key: 'nome' }}
            rowKey={(j) => j.key}
            conteggio={{ nome: t('job') }}
            columns={[
              ...((d.vendors?.length ?? 0) > 1
                ? [{ key: 'vendor', header: t('Fornitore'), value: (j: PrestazioniJob) => fornitore(j.vendor) }]
                : []),
              { key: 'nome', header: t('Job'), value: (j: PrestazioniJob) => j.nome, render: link },
              { key: 'esecuzioni', header: t('Esecuzioni'), value: (j) => j.esecuzioni },
              {
                key: 'riuscite', header: t('Riuscite'),
                value: (j) => (j.concluse ? j.riuscite / j.concluse : -1),
                render: (j) => (j.concluse ? `${Math.round((j.riuscite / j.concluse) * 100)}% (${j.riuscite}/${j.concluse})` : '—'),
              },
              {
                key: 'ultimo', header: t('Ultimo esito'), value: (j) => j.ultima.result ?? '',
                render: (j) => <EsitoBadge result={j.ultima.result} />,
              },
              { key: 'durata', header: t('Durata media'), value: (j) => j.durataMedia ?? 0, render: (j) => formatSecondi(j.durataMedia == null ? null : Math.round(j.durataMedia)) },
              { key: 'ultima_durata', header: t('Ultima durata'), value: (j) => j.ultimaDurata ?? 0, render: (j) => formatSecondi(j.ultimaDurata) },
              { key: 'velocita', header: t('Velocità media'), value: (j) => j.velocitaMedia ?? 0, render: (j) => formatVelocita(j.velocitaMedia) },
              { key: 'dati', header: t('Dati elaborati'), value: (j) => j.dati ?? 0, render: (j) => (j.dati == null ? '—' : formatBytes(j.dati)) },
            ]}
          />
        </>
      )}
      <Diagnostica voci={d?.diagnostics} />
    </Card>
  )
}

function JobsCard({ dati }: { dati: AsyncState<VeeamJobs> }) {
  const d = dati.data
  return (
    <Card
      title={t('Job di backup')}
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Aggiorno…') : t('Aggiorna')}
        </button>
      }
    >
      <ErrorBanner error={dati.error} />
      <Parziale motivi={d?.partial} />
      <NotaCache dati={d} />
      {dati.loading && !d ? (
        <Spinner label={t('Interrogo il backup…')} />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Backup non interrogabile.')}</p>
      ) : !(d.jobs ?? []).length ? (
        <EmptyState title={t('Nessun job')} hint={t('Il sistema di backup non ha job, oppure l’utenza non ha i permessi.')} />
      ) : (
        <DataTable
          rows={d.jobs ?? []}
          initialSort={{ key: 'name' }}
          rowKey={(j) => j.id}
          columns={[
            ...((d.vendors?.length ?? 0) > 1
              ? [{ key: 'vendor', header: t('Fornitore'), value: (j: VeeamJob) => fornitore(j.vendor) }]
              : []),
            {
              key: 'name', header: t('Job'), value: (j: VeeamJob) => j.name ?? '',
              render: (j: VeeamJob) => (
                <Link
                  to={`/backup/jobs/${encodeURIComponent(j.vendor ?? 'veeam')}/${encodeURIComponent(j.id)}` +
                    (j.name ? `?name=${encodeURIComponent(j.name)}` : '')}
                >
                  {j.name ?? j.id}
                </Link>
              ),
            },
            { key: 'type', header: t('Tipo'), value: (j) => j.type ?? '' },
            {
              key: 'last_result', header: t('Ultimo esito'),
              value: (j) => j.last_result ?? '',
              render: (j) => <EsitoBadge result={j.last_result} />,
            },
            { key: 'last_run', header: t('Ultima esecuzione'), value: (j) => j.last_run ?? '', render: (j) => j.last_run ?? '—' },
            { key: 'next_run', header: t('Prossima'), value: (j) => j.next_run ?? '', render: (j) => j.next_run ?? '—' },
            {
              key: 'objects_count', header: t('Oggetti'),
              value: (j) => j.objects_count ?? 0,
              render: (j) => (j.objects_count == null ? '—' : String(j.objects_count)),
            },
            {
              key: 'repository', header: t('Repository'), value: (j) => j.repository ?? '',
              // Su Commvault il piano è la retention e la repository è lo storage
              // pool del piano: due cose, mostrate come due cose.
              render: (j) => (
                <>
                  {j.repository ?? '—'}
                  {j.plan && <div className="text-muted" style={{ fontSize: 11 }}>{t('piano {p}', { p: j.plan })}</div>}
                </>
              ),
            },
          ]}
        />
      )}
      <Diagnostica voci={d?.diagnostics} />
    </Card>
  )
}

function RepositoriesCard({ dati }: { dati: AsyncState<VeeamRepositories> }) {
  const d = dati.data
  return (
    <Card
      title={t('Repository')}
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Aggiorno…') : t('Aggiorna')}
        </button>
      }
    >
      <ErrorBanner error={dati.error} />
      <Parziale motivi={d?.partial} />
      <NotaCache dati={d} />
      {dati.loading && !d ? (
        <Spinner label={t('Interrogo il backup…')} />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Backup non interrogabile.')}</p>
      ) : !(d.repositories ?? []).length ? (
        <EmptyState title={t('Nessuna repository')} hint="" />
      ) : (
        <DataTable
          rows={d.repositories ?? []}
          initialSort={{ key: 'name' }}
          rowKey={(r) => r.id}
          columns={[
            ...((d.vendors?.length ?? 0) > 1
              ? [{ key: 'vendor', header: t('Fornitore'), value: (r: VeeamRepository) => fornitore(r.vendor) }]
              : []),
            { key: 'name', header: t('Repository'), value: (r: VeeamRepository) => r.name ?? '' },
            { key: 'type', header: t('Tipo'), value: (r) => r.type ?? '' },
            { key: 'host', header: t('Host'), value: (r) => r.host ?? '', render: (r) => r.host ?? '—' },
            {
              key: 'capacity_gb', header: t('Capacità'),
              value: (r) => r.capacity_gb ?? 0,
              render: (r) => formatGB(r.capacity_gb),
            },
            {
              key: 'free_gb', header: t('Libero'),
              value: (r) => r.free_gb ?? 0,
              render: (r) => formatGB(r.free_gb),
            },
            {
              key: 'used_gb', header: t('Usato'),
              value: (r) => r.used_gb ?? 0,
              render: (r) => formatGB(r.used_gb),
            },
          ]}
        />
      )}
      <Diagnostica voci={d?.diagnostics} />
    </Card>
  )
}

function ObjectsCard({ dati }: { dati: AsyncState<VeeamObjects> }) {
  const d = dati.data
  return (
    <Card
      title={t('Oggetti protetti')}
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Aggiorno…') : t('Aggiorna')}
        </button>
      }
    >
      <ErrorBanner error={dati.error} />
      <Parziale motivi={d?.partial} />
      <NotaCache dati={d} />
      {dati.loading && !d ? (
        <Spinner label={t('Interrogo il backup…')} />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Backup non interrogabile.')}</p>
      ) : !(d.objects ?? []).length ? (
        <EmptyState title={t('Nessun oggetto protetto')} hint={t('Il sistema di backup non ha oggetti protetti da mostrare.')} />
      ) : (
        <DataTable
          rows={d.objects ?? []}
          conteggio={{ nome: t('oggetti protetti') }}
          initialSort={{ key: 'name' }}
          rowKey={(o) => o.id}
          columns={[
            ...((d.vendors?.length ?? 0) > 1
              ? [{ key: 'vendor', header: t('Fornitore'), value: (o: VeeamObject) => fornitore(o.vendor) }]
              : []),
            { key: 'name', header: t('Oggetto'), value: (o: VeeamObject) => o.name ?? '' },
            { key: 'type', header: t('Tipo'), value: (o) => o.type ?? '' },
            { key: 'platform', header: t('Piattaforma'), value: (o) => o.platform ?? '', render: (o) => o.platform ?? '—' },
            {
              key: 'restore_points', header: t('Punti di ripristino'),
              value: (o) => o.restore_points ?? 0,
              render: (o) => (o.restore_points == null ? '—' : String(o.restore_points)),
            },
          ]}
        />
      )}
      <Diagnostica voci={d?.diagnostics} />
    </Card>
  )
}

/**
 * Le VM della CMDB che **nessun** fornitore di backup copre: nessun oggetto
 * protetto porta il loro nome (intero o corto). È l'elenco da cui partire per
 * chiudere i buchi di protezione; ogni riga porta alla scheda del CI.
 */
function NonProtetteCard({ dati }: { dati: AsyncState<VeeamVmProtection> }) {
  const d = dati.data
  const scoperte = d?.unprotected ?? []
  return (
    <Card
      title={t('VM non protette')}
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Aggiorno…') : t('Aggiorna')}
        </button>
      }
    >
      <ErrorBanner error={dati.error} />
      <Parziale motivi={d?.partial} />
      <NotaCache dati={d} />
      {dati.loading && !d ? (
        <Spinner label={t('Interrogo il backup…')} />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Backup non interrogabile.')}</p>
      ) : (
        <>
          <div className="stats">
            <StatCard label={t('VM nella CMDB')} value={d.total ?? 0} />
            <StatCard label={t('Protette')} value={d.counts?.protected ?? 0} tone="ok" />
            <StatCard
              label={t('Non protette')}
              value={d.counts?.unprotected ?? 0}
              tone={(d.counts?.unprotected ?? 0) > 0 ? 'warn' : 'ok'}
            />
          </div>
          {!scoperte.length ? (
            <EmptyState title={t('Tutte le VM sono protette')} hint="" />
          ) : (
            <DataTable
              rows={scoperte}
              conteggio={{ nome: t('VM non protette') }}
              initialSort={{ key: 'name' }}
              rowKey={(v) => v.ci_id}
              columns={[
                {
                  key: 'name', header: t('VM'), value: (v: VeeamVmEntry) => v.name,
                  render: (v: VeeamVmEntry) => <Link to={`/cis/${v.ci_id}`}>{v.name || v.ci_id}</Link>,
                },
              ]}
            />
          )}
          <p className="muted">
            {t('Una VM risulta protetta quando un oggetto di backup di qualunque fornitore porta il suo nome (intero o corto). Una VM con nomi diversi nel backup e nella CMDB appare qui anche se è protetta.')}
          </p>
        </>
      )}
    </Card>
  )
}
